import { DiscoveryApi, FetchApi, OAuthApi } from '@backstage/core-plugin-api';
import { RITHUM_AGENT_START_TASK_ACTION_ID } from '@internal/plugin-rithum-agent-common';
import type { RithumAgentApi } from './RithumAgentApi';
import type {
  RithumAgentOptionsResponse,
  RithumAgentStartTaskInput,
} from './types';

export class DefaultRithumAgentClient implements RithumAgentApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly githubAuthApi?: OAuthApi,
  ) {}

  private async requestJson<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('rithum-agent');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        ...((init?.headers as Record<string, string>) ?? {}),
      },
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Rithum agent request failed: ${response.status} ${response.statusText} ${text}`,
      );
    }
    return (await response.json()) as T;
  }

  async startTask(
    input: RithumAgentStartTaskInput,
  ): Promise<{ message: string; workflowRunUrl?: string }> {
    let githubToken: string | undefined;
    if (this.githubAuthApi) {
      try {
        githubToken = await this.githubAuthApi.getAccessToken(['repo']);
      } catch {
        // Fall back to bot dispatch — token will be omitted
      }
    }

    const baseUrl = await this.discoveryApi.getBaseUrl('rithum-agent');
    const actionPath = `/.backstage/actions/v1/actions/${encodeURIComponent(
      RITHUM_AGENT_START_TASK_ACTION_ID,
    )}/invoke`;
    const response = await this.fetchApi.fetch(`${baseUrl}${actionPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...input, githubToken }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Rithum agent start-task failed: ${response.status} ${response.statusText} ${text}`,
      );
    }
    const data = (await response.json()) as {
      output?: { message?: string; workflowRunUrl?: string };
    };
    const message = data.output?.message;
    if (typeof message !== 'string') {
      throw new Error('Unexpected response from start-task action');
    }
    const { workflowRunUrl } = data.output ?? {};
    return {
      message,
      ...(typeof workflowRunUrl === 'string' ? { workflowRunUrl } : {}),
    };
  }

  async getOptions(): Promise<RithumAgentOptionsResponse> {
    return this.requestJson<RithumAgentOptionsResponse>('/config/options');
  }
}
